// Squelette pose par le lot A (§10.1).
//
// FICHIER PARTAGE A MARQUES (§10.2). Chaque lot n'ecrit qu'a sa propre marque.
//
// NOTE DU LOT F : le rendu est desormais enveloppe dans une fonction async.
// C'etait inevitable — MSW doit avoir intercepte le reseau AVANT le premier
// rendu, sinon les requetes de la premiere page partent dans le vide. La marque
// du lot A anticipait d'ailleurs ce demarrage. Voir docs/ECARTS.md.

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import App from './App'
import './index.css'
import { FournisseurToasts } from './components/ui/Toasts'
import { config } from './lib/config'
import { FournisseurSession } from './lib/auth'

// --- LOT F --- (demarrage MSW, fournisseurs TanStack Query et Router)
const clientRequetes = new QueryClient({
  defaultOptions: {
    queries: {
      // Les donnees de planning changent, mais pas a la seconde : un
      // rafraichissement a chaque retour d'onglet ferait clignoter la grille
      // sans rien apporter.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (nbEchecs, erreur) => {
        // Ne jamais reessayer une erreur d'authentification ou de droits :
        // elle ne se resoudra pas en insistant, et chaque tentative consomme
        // le quota de /auth/connexion.
        const statut = (erreur as { statut?: number }).statut
        if (statut === 401 || statut === 403 || statut === 404) return false
        return nbEchecs < 2
      },
    },
  },
})
// --- LOT G --- (enregistrement du service worker)

const racine = document.getElementById('root')

if (!racine) {
  // Sans cette garde, noUncheckedIndexedAccess et strictNullChecks obligeraient
  // a un `!` non verifie, et l'echec serait un message React illisible.
  throw new Error("L'element racine #root est introuvable dans index.html.")
}

async function demarrer(element: HTMLElement) {
  if (config.useMocks) {
    // Import dynamique : avec VITE_USE_MOCKS=false, MSW et le jeu de donnees
    // ne sont meme pas dans le bundle de production.
    const { worker } = await import('./mocks/browser')
    await worker.start({
      quiet: true,
      // La severite « error » est deliberee pour les appels d'API : un appel non
      // mocke doit exploser tout de suite, pas produire un ecran vide qu'on
      // cherchera une demi-heure.
      //
      // Mais elle ne doit juger QUE les appels d'API. MSW intercepte aussi la
      // navigation du navigateur, les modules servis par Vite et le favicon :
      // les traiter en erreur renvoie un 500 sur `GET /connexion` et empeche
      // l'application de s'afficher du tout.
      onUnhandledRequest(requete, afficher) {
        if (!new URL(requete.url).pathname.startsWith('/api/')) return
        afficher.error()
      },
    })
  }

  createRoot(element).render(
    <StrictMode>
      <QueryClientProvider client={clientRequetes}>
        <BrowserRouter>
          <FournisseurToasts>
            <FournisseurSession>
              <App />
            </FournisseurSession>
          </FournisseurToasts>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  )
}

void demarrer(racine)
